import { isLogisticsMeal } from "./schedule";
export type PersistableRouteStop = {
  name: string;
  venuesAfter?: SavedRouteVenue[];
};

export type SavedRouteVenue = {
  meal: "lunch" | "dinner" | "hotel" | "breakfast";
  name: string;
  start?: string;
  end?: string;
  lat: number;
  lng: number;
  placeId?: string;
  mapsUrl?: string;
  subtitle?: string;
  blurb?: string;
  website?: string;
  phone?: string;
};

type VenueScheduleBreak = {
  type: "break";
  meal: SavedRouteVenue["meal"] | "arrival" | "airport" | "checkout" | "departure_transfer" | "airport_margin" | "flight" | "departure";
  afterStopIdx: number;
  startAt: Date;
  endAt: Date;
};

type ResolvedVenue = {
  name: string;
  lat?: number;
  lng?: number;
  placeId?: string;
  googleMapsUri?: string;
  cuisine?: string;
  category?: string;
  blurb?: string;
  website?: string;
  phone?: string;
};

/** Pure persistence builder: keeps real meal/hotel venues without adding them as attractions. */
export function attachResolvedVenuesToSavedStops<T extends PersistableRouteStop>(input: {
  stops: T[];
  breaks: VenueScheduleBreak[];
  restaurants: Record<string, ResolvedVenue | null | "loading">;
  hotels: Record<string, ResolvedVenue | null | "loading">;
}): Array<T & PersistableRouteStop> {
  const stops = input.stops.map((stop) => ({ ...stop }));
  for (const brk of input.breaks) {
    const key = `${brk.meal}:${brk.afterStopIdx}`;
    const venue = brk.meal === "hotel" ? input.hotels[key] : isLogisticsMeal(brk.meal) ? undefined : input.restaurants[key];
    const stop = stops[brk.afterStopIdx];
    if (!stop || !venue || venue === "loading" || typeof venue.lat !== "number" || typeof venue.lng !== "number") continue;
    if (isLogisticsMeal(brk.meal)) continue;
    const saved: SavedRouteVenue = {
      meal: brk.meal,
      name: venue.name,
      start: brk.startAt.toISOString(),
      end: brk.endAt.toISOString(),
      lat: venue.lat,
      lng: venue.lng,
      ...(venue.placeId ? { placeId: venue.placeId } : {}),
      ...(venue.googleMapsUri ? { mapsUrl: venue.googleMapsUri } : {}),
      ...((venue.cuisine || venue.category) ? { subtitle: venue.cuisine || venue.category } : {}),
      ...(venue.blurb ? { blurb: venue.blurb } : {}),
      ...(venue.website ? { website: venue.website } : {}),
      ...(venue.phone ? { phone: venue.phone } : {}),
    };
    stop.venuesAfter = [...(stop.venuesAfter ?? []), saved];
  }
  return stops;
}